import { useState, useEffect, useCallback } from 'react';
import { apiRawFetch } from '../../main/client';
import type { TaxPeriod } from './useTaxPeriods';

interface TaxPeriodsSummary {
    hasPreset: boolean;
    overdueCount: number;
    upcomingPeriods: TaxPeriod[];
}

export default function useTaxPeriodsSummary(propertyId: number | undefined) {
    const [data, setData] = useState<TaxPeriodsSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchSummary = useCallback(async () => {
        if (!propertyId) {
            setData(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await apiRawFetch(
                `api/properties/${propertyId}/tax-periods/summary`
            );

            if (!response.ok) {
                throw new Error('Failed to fetch tax periods summary');
            }

            const { data: summaryData } = await response.json();
            setData(summaryData);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to fetch summary';
            setError(message);
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [propertyId]);

    useEffect(() => {
        fetchSummary();
    }, [fetchSummary]);

    return {
        data,
        loading,
        error,
        refetch: fetchSummary,
    };
}
